import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../../firebase/config';
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc } from 'firebase/firestore';

// --- ICONOS SVG LIMPIOS (Corregidos a escala estándar w-4 h-4 de Tailwind) ---
const IconChevronLeft = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>;
const IconChevronRight = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>;
const IconTrash = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>;
const IconEdit = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.536l12.232-12.232z" /></svg>;

const ReglasRenovacion = () => {
    const navigate = useNavigate();
    const [reglas, setReglas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [isEditing, setIsEditing] = useState(null);

    // Notificaciones y Confirmaciones controladas por estados (No Alerts / No Confirms nativos)
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    const [confirmDelete, setConfirmDelete] = useState({ show: false, id: null });

    // Maestros para los selectores con mapeo de IDs de Firestore
    const [dbCompanias, setDbCompanias] = useState([]);
    const [dbRiesgos, setDbRiesgos] = useState([]);
    const [subriesgosDisponibles, setSubriesgosDisponibles] = useState([]);

    // Estados para los Filtros Dinámicos
    const [filtroCompania, setFiltroCompania] = useState('');
    const [filtroRiesgo, setFiltroRiesgo] = useState('');

    // Estados para la Paginación
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const [formData, setFormData] = useState({
        companiaId: '',
        compania: '',
        riesgoId: '',
        riesgo: '',
        subriesgo: '', // Opcional para herencia general
        formaRenovacion: 'MANUAL' // MANUAL o AUTOMÁTICA
    });

    const reglasRef = collection(db, 'public/data/reglas_renovacion');

    // Muestra alerta fluida autolimpiable
    const triggerToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 4000);
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            // Cargar Reglas de Renovación
            const snap = await getDocs(reglasRef);
            const reglasData = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            reglasData.sort((a, b) => (a.compania || '').localeCompare(b.compania || ''));
            setReglas(reglasData);

            // Cargar Maestros de Compañías y Riesgos
            const [ciasSnap, riesgosSnap] = await Promise.all([
                getDocs(collection(db, 'public/data/companias')),
                getDocs(collection(db, 'public/data/riesgos'))
            ]);

            const companiasData = ciasSnap.docs.map(d => ({
                id: d.id,
                ...d.data()
            })).sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));
            setDbCompanias(companiasData);

            const riesgosData = riesgosSnap.docs.map(d => ({ 
                id: d.id, 
                ...d.data() 
            })).sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));
            setDbRiesgos(riesgosData);

        } catch (error) {
            console.error("Error cargando datos de configuración:", error);
            triggerToast("Error al sincronizar datos maestros.", "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, []);

    // Reiniciar paginación al cambiar los filtros dinámicos
    useEffect(() => {
        setCurrentPage(1);
    }, [filtroCompania, filtroRiesgo]);

    // Buscar y poblar subriesgos del ramo seleccionado
    useEffect(() => {
        if (formData.riesgoId) {
            const rObj = dbRiesgos.find(r => r.id === formData.riesgoId);
            setSubriesgosDisponibles(rObj?.subriesgos || []);
        } else {
            setSubriesgosDisponibles([]);
        }
    }, [formData.riesgoId, dbRiesgos]);

    // --- LÓGICA DE FILTRADO DINÁMICO EN MEMORIA (0 ms / Firestore Friendly) ---
    const reglasFiltradas = useMemo(() => {
        return reglas.filter(rg => {
            const matchCia = filtroCompania === '' || rg.compania === filtroCompania;
            const matchRiesgo = filtroRiesgo === '' || rg.riesgo === filtroRiesgo;
            return matchCia && matchRiesgo;
        });
    }, [reglas, filtroCompania, filtroRiesgo]);

    // --- LÓGICA DE PAGINACIÓN ---
    const paginatedReglas = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return reglasFiltradas.slice(startIndex, startIndex + itemsPerPage);
    }, [reglasFiltradas, currentPage]);

    const totalPages = Math.ceil(reglasFiltradas.length / itemsPerPage);

    // --- LOGICA MODAL ---
    const handleOpenModal = (item = null) => {
        if (item) {
            setFormData({
                companiaId: item.companiaId || '',
                compania: item.compania || '',
                riesgoId: item.riesgoId || '',
                riesgo: item.riesgo || '',
                subriesgo: item.subriesgo || '',
                formaRenovacion: item.formaRenovacion || 'MANUAL'
            });
            setIsEditing(item.id);
        } else {
            setFormData({ companiaId: '', compania: '', riesgoId: '', riesgo: '', subriesgo: '', formaRenovacion: 'MANUAL' });
            setIsEditing(null);
        }
        setShowModal(true);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            // Estructura híbrida relacional robusta (ID + Nombre)
            const payload = {
                companiaId: formData.companiaId,
                compania: formData.compania,
                riesgoId: formData.riesgoId,
                riesgo: formData.riesgo,
                subriesgo: formData.subriesgo || '', // En blanco representa regla general (Fallback)
                formaRenovacion: formData.formaRenovacion
            };

            if (isEditing) {
                await updateDoc(doc(db, 'public/data/reglas_renovacion', isEditing), payload);
                triggerToast("Regla de renovación actualizada correctamente.");
            } else {
                await addDoc(reglasRef, payload);
                triggerToast("Nueva regla de renovación registrada con éxito.");
            }
            setShowModal(false);
            fetchData();
        } catch (error) {
            console.error("Error al guardar regla:", error);
            triggerToast("Error al guardar la regla de renovación.", "error");
        }
    };

    const handleConfirmDelete = (id) => {
        setConfirmDelete({ show: true, id });
    };

    const handleDelete = async () => {
        const { id } = confirmDelete;
        if (!id) return;
        try {
            await deleteDoc(doc(db, 'public/data/reglas_renovacion', id));
            setConfirmDelete({ show: false, id: null });
            triggerToast("Regla de renovación eliminada correctamente.");
            fetchData();
        } catch (error) {
            console.error("Error al eliminar regla:", error);
            triggerToast("Error al intentar eliminar la regla.", "error");
        }
    };

    const handleCompaniaChange = (e) => {
        const selectedId = e.target.value;
        const selectedObj = dbCompanias.find(c => c.id === selectedId);
        setFormData(prev => ({
            ...prev,
            companiaId: selectedId,
            compania: selectedObj ? selectedObj.nombre : ''
        }));
    };

    const handleRiesgoChange = (e) => {
        const selectedId = e.target.value;
        const selectedObj = dbRiesgos.find(r => r.id === selectedId);
        setFormData(prev => ({
            ...prev,
            riesgoId: selectedId,
            riesgo: selectedObj ? selectedObj.nombre : '',
            subriesgo: ''
        }));
    };

    return (
        <div className="p-8 bg-gray-50 min-h-screen font-sans text-gray-800 text-left relative">
            
            {/* NOTIFICACION FLUIDA (REEMPLAZA ALERTS) */}
            {toast.show && (
                <div className={`fixed top-6 right-6 p-4 rounded-2xl shadow-2xl border text-xs font-bold uppercase tracking-wide z-[2000] animate-fade-in flex items-center gap-3
                    ${toast.type === 'error' ? 'bg-red-50 border-red-200 text-red-700' : 'bg-emerald-50 border-emerald-200 text-emerald-700'}`}>
                    <span className="text-lg">{toast.type === 'error' ? '⚠️' : '✓'}</span>
                    {toast.message}
                </div>
            )}

            <div className="max-w-6xl mx-auto">
                
                {/* ENCABEZADO */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
                    <div className="text-left">
                        <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">Reglas de Renovación</h1>
                        <p className="text-gray-500 mt-1">Configura el comportamiento automático o manual por producto y compañía (Herencia General).</p>
                    </div>
                    <div className="flex gap-3 shrink-0">
                        <button onClick={() => navigate('/AdminDashboard')} className="bg-white border border-gray-300 text-gray-700 px-5 py-2.5 rounded-xl font-bold shadow-sm hover:bg-gray-50 transition text-xs uppercase tracking-wider">Volver</button>
                        <button onClick={() => handleOpenModal()} className="bg-emerald-600 text-white px-5 py-2.5 rounded-xl font-bold shadow hover:bg-emerald-700 transition text-xs flex gap-2 items-center uppercase tracking-wider active:scale-95">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
                            Nueva Regla
                        </button>
                    </div>
                </div>

                {/* --- BARRA DE FILTROS DINÁMICOS --- */}
                <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-wider block mb-1">Filtrar por Compañía</label>
                        <select 
                            value={filtroCompania} 
                            onChange={(e) => setFiltroCompania(e.target.value)} 
                            className="w-full border border-gray-200 rounded-xl p-2.5 text-xs font-bold bg-gray-50 text-slate-700 outline-none focus:ring-2 focus:ring-emerald-500 transition-all cursor-pointer"
                        >
                            <option value="">TODAS LAS COMPAÑÍAS</option>
                            {dbCompanias.map(c => <option key={c.id} value={c.nombre}>{c.nombre}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-wider block mb-1">Filtrar por Ramo / Riesgo</label>
                        <select 
                            value={filtroRiesgo} 
                            onChange={(e) => setFiltroRiesgo(e.target.value)} 
                            className="w-full border border-gray-200 rounded-xl p-2.5 text-xs font-bold bg-gray-50 text-slate-700 outline-none focus:ring-2 focus:ring-emerald-500 transition-all cursor-pointer"
                        >
                            <option value="">TODOS LOS RAMOS / RIESGOS</option>
                            {dbRiesgos.map(r => <option key={r.id} value={r.nombre}>{r.nombre}</option>)}
                        </select>
                    </div>
                </div>

                {/* --- TABLA DE REGLAS --- */}
                <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden flex flex-col">
                    <table className="w-full text-left text-sm border-collapse">
                        <thead>
                            <tr className="bg-gray-50 border-b border-gray-200 text-left">
                                <th className="p-4 px-6 text-[10px] font-black text-gray-400 uppercase tracking-widest">Compañía</th>
                                <th className="p-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Riesgo / Producto</th>
                                <th className="p-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Subriesgo</th>
                                <th className="p-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Forma Renovación</th>
                                <th className="p-4 px-6 text-[10px] font-black text-gray-400 uppercase tracking-widest text-center">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 text-left">
                            {loading ? (
                                <tr><td colSpan="5" className="p-12 text-center text-gray-400 font-bold">Cargando reglas de renovación...</td></tr>
                            ) : paginatedReglas.length === 0 ? (
                                <tr><td colSpan="5" className="p-12 text-center text-gray-400 font-bold italic">No se encontraron reglas configuradas que coincidan con los filtros.</td></tr>
                            ) : (
                                paginatedReglas.map(reg => (
                                    <tr key={reg.id} className="hover:bg-slate-50/50 transition-colors">
                                        <td className="p-4 px-6 font-bold text-gray-900 uppercase text-xs">{reg.compania}</td>
                                        <td className="p-4 font-bold text-gray-700 text-xs">{reg.riesgo}</td>
                                        <td className="p-4">
                                            {reg.subriesgo ? (
                                                <span className="text-blue-800 font-bold bg-blue-50 border border-blue-100 px-2.5 py-1 rounded-lg text-xs uppercase">
                                                    {reg.subriesgo}
                                                </span>
                                            ) : (
                                                <span className="text-gray-400 font-bold italic text-xs tracking-wide">
                                                    (GENERAL - Aplica a todo el Ramo)
                                                </span>
                                            )}
                                        </td>
                                        <td className="p-4 text-left">
                                            <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase border ${
                                                reg.formaRenovacion === 'AUTOMÁTICA' 
                                                ? 'bg-emerald-50 text-emerald-700 border-emerald-100' 
                                                : 'bg-slate-100 text-slate-500 border-slate-200'
                                            }`}>
                                                {reg.formaRenovacion}
                                            </span>
                                        </td>
                                        <td className="p-4 text-center px-6">
                                            <div className="flex justify-center gap-2">
                                                <button onClick={() => handleOpenModal(reg)} className="p-1.5 text-blue-500 hover:bg-blue-50 rounded-lg transition-all" title="Editar"><IconEdit /></button>
                                                <button onClick={() => handleConfirmDelete(reg.id)} className="p-1.5 text-red-400 hover:bg-red-50 rounded-lg transition-all" title="Eliminar"><IconTrash /></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>

                    {/* --- CONTROLES DE PAGINACIÓN --- */}
                    {totalPages > 1 && (
                        <div className="h-14 bg-slate-50 border-t border-gray-100 flex justify-between items-center px-8 text-xs font-bold text-gray-500 shrink-0">
                            <span>Mostrando {paginatedReglas.length} de {reglasFiltradas.length} reglas filtradas</span>
                            <div className="flex gap-2">
                                <button 
                                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))} 
                                    disabled={currentPage === 1} 
                                    className="px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-30 uppercase tracking-wider text-[10px] font-black transition-all shadow-sm flex items-center gap-1 cursor-pointer"
                                >
                                    <IconChevronLeft /> Anterior
                                </button>
                                <span className="flex items-center px-4 font-black text-emerald-800 bg-emerald-50 border border-emerald-100 rounded-lg">Página {currentPage} de {totalPages}</span>
                                <button 
                                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} 
                                    disabled={currentPage === totalPages} 
                                    className="px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-30 uppercase tracking-wider text-[10px] font-black transition-all shadow-sm flex items-center gap-1 cursor-pointer"
                                >
                                    Siguiente <IconChevronRight />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* MODAL CREAR / EDITAR */}
            {showModal && (
                <div className="fixed inset-0 bg-gray-900 bg-opacity-60 flex items-center justify-center p-4 z-[1200] backdrop-blur-sm transition-opacity text-left">
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden animate-fade-in border border-gray-100">
                        <div className="bg-emerald-600 px-8 py-5 flex justify-between items-center text-white text-left">
                            <div className="text-left">
                                <h3 className="font-bold text-xl leading-none text-left">{isEditing ? 'Editar Regla' : 'Nueva Regla'}</h3>
                                <p className="text-emerald-100 text-[10px] mt-1.5 font-bold uppercase tracking-wider text-left">Políticas de Renovación</p>
                            </div>
                            <button onClick={() => setShowModal(false)} className="p-2 hover:bg-white/20 rounded-full transition-colors text-white font-mono">✕</button>
                        </div>
                        
                        <form onSubmit={handleSave} className="p-8 space-y-5 text-left">
                            <div>
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-1.5">Compañía Aseguradora *</label>
                                <select 
                                    value={formData.companiaId} 
                                    onChange={handleCompaniaChange} 
                                    className="w-full border border-gray-300 px-4 py-2.5 rounded-xl outline-none font-bold text-gray-700 bg-white cursor-pointer text-xs"
                                    required
                                >
                                    <option value="">Seleccione...</option>
                                    {dbCompanias.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                                </select>
                            </div>

                            <div className="grid grid-cols-2 gap-4 text-left">
                                <div>
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-1.5">Riesgo Principal *</label>
                                    <select 
                                        value={formData.riesgoId} 
                                        onChange={handleRiesgoChange} 
                                        className="w-full border border-gray-300 px-4 py-2.5 rounded-xl outline-none font-bold text-gray-700 bg-white cursor-pointer text-xs"
                                        required
                                    >
                                        <option value="">Seleccione...</option>
                                        {dbRiesgos.map(r => <option key={r.id} value={r.id}>{r.nombre}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-1.5">Subriesgo(Opcional)</label>
                                    <select 
                                        value={formData.subriesgo} 
                                        onChange={(e) => setFormData({...formData, subriesgo: e.target.value})} 
                                        className="w-full border border-gray-300 px-4 py-2.5 rounded-xl outline-none font-bold text-gray-700 bg-white cursor-pointer disabled:bg-gray-100 text-xs"
                                        disabled={!formData.riesgoId || subriesgosDisponibles.length === 0}
                                    >
                                        <option value="">(GENERAL - Aplica a todo el Ramo)</option>
                                        {subriesgosDisponibles.map((s, idx) => <option key={idx} value={s}>{s}</option>)}
                                    </select>
                                </div>
                            </div>

                            <div className="bg-slate-50 p-5 rounded-2xl border border-slate-200 text-left">
                                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block mb-3 text-center">Forma de Renovación Aplicable</label>
                                <div className="grid grid-cols-2 gap-3 text-left">
                                    <button 
                                        type="button"
                                        onClick={() => setFormData({...formData, formaRenovacion: 'MANUAL'})}
                                        className={`px-4 py-3 rounded-xl border font-black text-[10px] transition-all uppercase tracking-wider ${
                                            formData.formaRenovacion === 'MANUAL' 
                                            ? 'bg-slate-800 text-white border-slate-800 shadow-lg' 
                                            : 'bg-white text-slate-400 border-slate-200 hover:border-slate-300'
                                        }`}
                                    >
                                        MANUAL
                                    </button>
                                    <button 
                                        type="button"
                                        onClick={() => setFormData({...formData, formaRenovacion: 'AUTOMÁTICA'})}
                                        className={`px-4 py-3 rounded-xl border font-black text-[10px] transition-all uppercase tracking-wider ${
                                            formData.formaRenovacion === 'AUTOMÁTICA' 
                                            ? 'bg-emerald-600 text-white border-emerald-600 shadow-lg' 
                                            : 'bg-white text-slate-400 border-slate-200 hover:border-emerald-200'
                                        }`}
                                    >
                                        AUTOMÁTICA
                                    </button>
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 pt-4 border-t">
                                <button type="button" onClick={() => setShowModal(false)} className="px-6 py-2.5 rounded-xl border border-gray-300 text-gray-600 font-bold hover:bg-gray-100 transition-all text-xs uppercase tracking-wider">Cancelar</button>
                                <button type="submit" className="px-8 py-2.5 rounded-xl bg-emerald-600 text-white font-bold hover:bg-emerald-700 shadow-lg transition-all text-xs uppercase tracking-wider active:scale-95">Guardar Regla</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* DIÁLOGO MODAL ELIMINAR (Sustituye window.confirm nativo de forma fluida) */}
            {confirmDelete.show && (
                <div className="fixed inset-0 bg-gray-900 bg-opacity-60 flex items-center justify-center p-4 z-[1300] backdrop-blur-sm transition-opacity text-left">
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden border border-gray-100 text-center p-6 space-y-4">
                        <div className="w-12 h-12 rounded-full bg-red-50 text-red-600 flex items-center justify-center mx-auto text-xl font-bold">⚠️</div>
                        <div className="space-y-1">
                            <h4 className="text-md font-black text-slate-800 uppercase tracking-tight">¿Eliminar Regla?</h4>
                            <p className="text-xs text-slate-500 font-medium">Esta acción no se puede deshacer. Se removerá la directiva de renovación de la base de datos de producción.</p>
                        </div>
                        <div className="flex gap-3 pt-2">
                            <button 
                                type="button" 
                                onClick={() => setConfirmDelete({ show: false, id: null })} 
                                className="flex-1 py-2.5 border rounded-xl font-bold text-gray-500 text-xs hover:bg-slate-50 uppercase tracking-wider"
                            >
                                Cancelar
                            </button>
                            <button 
                                type="button" 
                                onClick={handleDelete} 
                                className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold text-xs uppercase tracking-wider shadow-lg active:scale-95"
                            >
                                Confirmar
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            <style>{`
                .custom-scrollbar::-webkit-scrollbar { width: 4px; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
                .animate-fade-in { animation: fadeIn 0.3s ease-out; }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(5px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>
        </div>
    );
};

export default ReglasRenovacion;